<script setup lang="ts">
import { quoteLineAmounts, type CatalogProduct, type QuoteDraftLine, type Supplier } from '@cockpit/shared';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { subcontractingApi } from '../../api/subcontracting';
import { supplierOptions } from '../../subcontracting';
import SearchSelect from '../SearchSelect.vue';
import '../../styles/subcontracting.css';
import { eur, eurCents } from '../../format';
import {
  centsInput,
  lineFromProduct,
  numberInput,
  offCatalogLine,
  parseFrNumber,
  searchCatalog,
  takeClosestProduct,
} from '../../quoteDraft';

/**
 * Lot AT [4, 5] : les lignes du brouillon de devis. Chaque ligne : produit du catalogue Axonaut (ou prestation hors
 * catalogue, jamais bloquée : « Créer le produit dans Axonaut » ou « Prendre le produit le plus proche »), quantité,
 * prix unitaire HT, remise, total ; sous la ligne, « Pourquoi » (phrase des notes) et « à vérifier ». Recherche dans
 * le catalogue pour ajouter une ligne. Les modifications remontent tout de suite (`update`).
 */
const props = defineProps<{
  lines: QuoteDraftLine[];
  catalog: readonly CatalogProduct[];
  readonly?: boolean;
}>();
const emit = defineEmits<{ update: [lines: QuoteDraftLine[]]; toast: [text: string] }>();

const byId = computed(() => new Map(props.catalog.map((p) => [p.id, p])));
const editing = ref<Set<string>>(new Set());

function replace(index: number, line: QuoteDraftLine): void {
  const next = [...props.lines];
  next[index] = line;
  emit('update', next);
}

function onNumber(
  index: number,
  field: 'quantity' | 'unitPriceCents' | 'discountPercent',
  raw: string,
): void {
  const value = parseFrNumber(raw);
  if (value === null) return;
  const line = props.lines[index]!;
  if (field === 'unitPriceCents')
    replace(index, { ...line, unitPriceCents: Math.max(0, Math.round(value * 100)) });
  else if (field === 'quantity') replace(index, { ...line, quantity: value > 0 ? value : line.quantity });
  else replace(index, { ...line, discountPercent: Math.min(100, Math.max(0, value)) });
}

/** Lot BJ : la description de la ligne de frais dit tout le projet : la zone grandit avec elle (12 lignes au plus). */
function descriptionRows(description: string | null): number {
  return Math.min(12, Math.max(2, (description ?? '').split('\n').length));
}

function onText(index: number, field: 'name' | 'description', raw: string): void {
  const line = props.lines[index]!;
  if (field === 'name') {
    if (raw.trim() === '') return;
    replace(index, { ...line, name: raw.slice(0, 300) });
  } else replace(index, { ...line, description: raw.trim() === '' ? null : raw.slice(0, 4000) });
}

// ------------------------------------------------------------------ Lot AY : lignes sous-traitées

const suppliers = ref<Supplier[]>([]);
const supplierChoices = computed(() => supplierOptions(suppliers.value));
let suppliersLoading: Promise<void> | null = null;
function ensureSuppliers(): Promise<void> {
  if (suppliers.value.length > 0) return Promise.resolve();
  suppliersLoading ??= subcontractingApi
    .suppliers()
    .then((res) => {
      suppliers.value = res.items;
    })
    .catch(() => undefined)
    .finally(() => {
      suppliersLoading = null;
    });
  return suppliersLoading;
}
onMounted(() => {
  if (props.lines.some((l) => l.subcontract)) void ensureSuppliers();
});

/** « Sous-traité » : sous-traitant (fournisseur Axonaut) et coût payé, prérempli avec le total de la ligne. */
function toggleSub(index: number, checked: boolean): void {
  const line = props.lines[index]!;
  if (!checked) {
    replace(index, { ...line, subcontract: null });
    return;
  }
  void ensureSuppliers();
  const last = props.lines.find((l) => l.subcontract?.supplierId)?.subcontract?.supplierId ?? null;
  replace(index, { ...line, subcontract: { supplierId: last, costCents: quoteLineAmounts(line).netCents } });
}
function setSubSupplier(index: number, supplierId: string | null): void {
  const line = props.lines[index]!;
  if (line.subcontract) replace(index, { ...line, subcontract: { ...line.subcontract, supplierId } });
}
function setSubCost(index: number, raw: string): void {
  const value = parseFrNumber(raw);
  const line = props.lines[index]!;
  if (value === null || !line.subcontract) return;
  replace(index, {
    ...line,
    subcontract: { ...line.subcontract, costCents: Math.max(0, Math.round(value * 100)) },
  });
}

function remove(index: number): void {
  const next = props.lines.filter((_, i) => i !== index);
  emit('update', next);
  emit('toast', 'Ligne retirée du brouillon.');
}

function toggleEdit(key: string): void {
  const next = new Set(editing.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  editing.value = next;
}

function confirmNewProduct(index: number): void {
  const line = props.lines[index]!;
  replace(index, { ...line, newProductConfirmed: true });
  emit('toast', `« ${line.name} » sera créé dans le catalogue Axonaut à la validation.`);
}

function takeClosest(index: number): void {
  const line = props.lines[index]!;
  const product = line.closestProductId ? byId.value.get(line.closestProductId) : undefined;
  if (!product) return;
  replace(index, takeClosestProduct(line, product));
  const next = new Set(editing.value);
  next.add(line.key);
  editing.value = next;
  emit('toast', `Ligne rattachée à « ${product.name} » : modifie son titre, sa description et son prix.`);
}

/** Produit le plus proche à choisir dans le catalogue (ligne hors catalogue sans proposition). */
function setClosest(index: number, productId: string): void {
  const line = props.lines[index]!;
  replace(index, { ...line, closestProductId: productId || null });
}

// ------------------------------------------------------------------ Recherche dans le catalogue

const query = ref('');
const pickerOpen = ref(false);
const picker = ref<HTMLElement | null>(null);
const found = computed(() => searchCatalog(props.catalog, query.value));
const activeProducts = computed(() => props.catalog.filter((p) => !p.disabled));

function add(product: CatalogProduct): void {
  emit('update', [...props.lines, lineFromProduct(product)]);
  query.value = '';
  pickerOpen.value = false;
  emit('toast', `« ${product.name} » ajouté au brouillon.`);
}

function addOffCatalog(): void {
  const line = offCatalogLine();
  emit('update', [...props.lines, line]);
  const next = new Set(editing.value);
  next.add(line.key);
  editing.value = next;
}

function onOutside(event: MouseEvent): void {
  if (picker.value && !picker.value.contains(event.target as Node)) pickerOpen.value = false;
}
onMounted(() => document.addEventListener('mousedown', onOutside));
onBeforeUnmount(() => document.removeEventListener('mousedown', onOutside));

function lineTotal(line: QuoteDraftLine): { net: string; note: string | null } {
  const a = quoteLineAmounts(line);
  return {
    net: a.netCents % 100 === 0 ? eur(a.netCents) : eurCents(a.netCents),
    note:
      line.discountPercent >= 100
        ? 'offert'
        : line.discountPercent > 0
          ? `− ${numberInput(line.discountPercent)} %`
          : null,
  };
}
</script>

<template>
  <div class="q-lines-wrap">
    <div class="qhead" aria-hidden="true">
      <span>Produit du catalogue Axonaut</span><span>Qté</span><span>Prix unit. HT</span><span>Remise</span
      ><span>Total HT</span><span></span>
    </div>
    <p v-if="lines.length === 0" class="muted q-empty">Aucune ligne pour l’instant.</p>
    <div class="qlines" data-testid="quote-lines">
      <div
        v-for="(line, i) in lines"
        :key="line.key"
        class="qline"
        :class="{ 'has-doubt': !!line.doubt, 'is-added': line.addedBy === 'user', 'is-off': !line.productId }"
        data-testid="quote-line"
        :data-product-id="line.productId ?? ''"
      >
        <div class="prod">
          <b data-testid="quote-line-name">{{ line.name }}</b>
          <small v-if="line.productId"
            >catalogue Axonaut{{ line.productCode ? ` · ${line.productCode}` : ''
            }}{{ line.productName && line.productName !== line.name ? ` · ${line.productName}` : '' }}</small
          >
          <small v-else class="q-off">hors catalogue</small>
          <button
            v-if="!readonly"
            class="linkish q-edit"
            type="button"
            :aria-expanded="editing.has(line.key) ? 'true' : 'false'"
            data-testid="quote-line-edit"
            @click="toggleEdit(line.key)"
          >
            {{ editing.has(line.key) ? 'Fermer' : 'Titre et description' }}
          </button>
        </div>
        <label
          ><span class="ml">Qté</span
          ><input
            type="text"
            inputmode="decimal"
            :value="numberInput(line.quantity)"
            :disabled="readonly"
            aria-label="Quantité"
            data-testid="quote-line-qty"
            @change="onNumber(i, 'quantity', ($event.target as HTMLInputElement).value)"
        /></label>
        <label
          ><span class="ml">Prix unit. HT</span
          ><input
            type="text"
            inputmode="decimal"
            :value="centsInput(line.unitPriceCents)"
            :disabled="readonly"
            aria-label="Prix unitaire HT"
            data-testid="quote-line-price"
            @change="onNumber(i, 'unitPriceCents', ($event.target as HTMLInputElement).value)"
        /></label>
        <label
          ><span class="ml">Remise %</span
          ><input
            type="text"
            inputmode="decimal"
            :value="numberInput(line.discountPercent)"
            :disabled="readonly"
            aria-label="Remise en pourcentage"
            data-testid="quote-line-discount"
            @change="onNumber(i, 'discountPercent', ($event.target as HTMLInputElement).value)"
        /></label>
        <span class="tot" data-testid="quote-line-total"
          >{{ lineTotal(line).net
          }}<small v-if="lineTotal(line).note">{{ lineTotal(line).note }}</small></span
        >
        <button
          v-if="!readonly"
          class="del"
          type="button"
          aria-label="Retirer la ligne"
          data-testid="quote-line-remove"
          @click="remove(i)"
        >
          ×
        </button>
        <div v-if="editing.has(line.key) && !readonly" class="q-edit-box">
          <label class="field"
            >Titre sur le devis
            <input
              type="text"
              :value="line.name"
              data-testid="quote-line-title"
              @change="onText(i, 'name', ($event.target as HTMLInputElement).value)"
          /></label>
          <label class="field"
            >Description (facultative)
            <textarea
              :rows="descriptionRows(line.description)"
              :value="line.description ?? ''"
              data-testid="quote-line-description"
              @change="onText(i, 'description', ($event.target as HTMLTextAreaElement).value)"
            ></textarea>
          </label>
        </div>
        <div v-if="!line.productId" class="q-off-box" data-testid="quote-line-offcatalog">
          <template v-if="line.newProductConfirmed">
            <span
              >Ce produit sera <b>créé dans le catalogue Axonaut</b> à la validation (une étape que tu pourras
              décocher).</span
            >
          </template>
          <template v-else>
            <span>Pas dans le catalogue Axonaut. Deux choix, rien n’est bloqué :</span>
            <div class="row">
              <button
                class="btn sm"
                type="button"
                :disabled="readonly"
                data-testid="quote-line-create-product"
                @click="confirmNewProduct(i)"
              >
                Créer le produit dans Axonaut
              </button>
              <button
                class="btn sm"
                type="button"
                :disabled="readonly || !line.closestProductId || !byId.get(line.closestProductId)"
                data-testid="quote-line-closest"
                @click="takeClosest(i)"
              >
                Prendre le produit le plus proche{{
                  line.closestProductId && byId.get(line.closestProductId)
                    ? ` : ${byId.get(line.closestProductId)!.name}`
                    : ''
                }}
              </button>
              <label class="q-closest-pick">
                <span class="sr-only">Produit le plus proche</span>
                <select
                  :value="line.closestProductId ?? ''"
                  :disabled="readonly"
                  aria-label="Produit le plus proche"
                  data-testid="quote-line-closest-select"
                  @change="setClosest(i, ($event.target as HTMLSelectElement).value)"
                >
                  <option value="">Choisir le produit le plus proche…</option>
                  <option v-for="p in activeProducts" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
              </label>
            </div>
          </template>
        </div>
        <div class="q-sub" :class="{ on: !!line.subcontract }" data-testid="quote-line-sub">
          <label class="q-sub-check"
            ><input
              type="checkbox"
              :checked="!!line.subcontract"
              :disabled="readonly"
              data-testid="quote-line-sub-check"
              @change="toggleSub(i, ($event.target as HTMLInputElement).checked)"
            />
            Sous-traité</label
          >
          <template v-if="line.subcontract">
            <SearchSelect
              class="ss"
              :model-value="line.subcontract.supplierId"
              :options="supplierChoices"
              aria-label="Sous-traitant"
              placeholder="Sous-traitant (fournisseur Axonaut)…"
              :clearable="false"
              :disabled="readonly"
              empty-text="Aucun fournisseur Axonaut : créez-le dans Axonaut, puis synchronisez"
              testid="quote-line-sub-supplier"
              @update:model-value="(v) => setSubSupplier(i, v)"
            />
            <span>payé</span>
            <input
              class="st-amount"
              type="text"
              inputmode="decimal"
              aria-label="Coût payé au sous-traitant"
              :value="centsInput(line.subcontract.costCents)"
              :disabled="readonly"
              data-testid="quote-line-sub-cost"
              @change="setSubCost(i, ($event.target as HTMLInputElement).value)"
            />
            <span>€ HT</span>
          </template>
        </div>
        <div class="why">
          <template v-if="line.reason"
            ><span>Pourquoi :</span><q>{{ line.reason }}</q></template
          >
          <span v-else-if="line.addedBy === 'user'">Ajoutée par toi.</span>
          <template v-if="line.doubt"
            ><span class="chip c-warn">à vérifier</span
            ><span class="doubt-t" data-testid="quote-line-doubt">{{ line.doubt }}</span></template
          >
        </div>
      </div>
    </div>
    <div v-if="!readonly" ref="picker" class="picker" :class="{ open: pickerOpen }">
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
        <circle cx="7" cy="7" r="4.5" />
        <path d="M10.5 10.5L14 14" stroke-linecap="round" />
      </svg>
      <input
        v-model="query"
        type="text"
        placeholder="+ Ajouter un produit du catalogue Axonaut (tape « photo », « page »…)"
        aria-label="Rechercher un produit du catalogue Axonaut"
        autocomplete="off"
        data-testid="quote-picker"
        @focus="pickerOpen = true"
        @input="pickerOpen = true"
        @keydown.escape="pickerOpen = false"
      />
      <div class="picker-list" role="listbox" aria-label="Produits du catalogue">
        <button
          v-for="p in found"
          :key="p.id"
          type="button"
          role="option"
          data-testid="quote-picker-option"
          @click="add(p)"
        >
          <span>{{ p.name }}</span
          ><small>{{ p.code ?? 'sans code' }}</small
          ><span class="pr">{{ p.priceCents % 100 === 0 ? eur(p.priceCents) : eurCents(p.priceCents) }}</span>
        </button>
        <div v-if="found.length === 0" class="none">
          {{
            catalog.length === 0
              ? 'Catalogue vide : la synchro Axonaut ne l’a pas encore lu.'
              : 'Aucun produit du catalogue ne correspond.'
          }}
        </div>
      </div>
    </div>
    <div v-if="!readonly" class="row">
      <button class="linkish" type="button" data-testid="quote-add-offcatalog" @click="addOffCatalog">
        + Prestation hors catalogue
      </button>
    </div>
    <p class="lock-note">
      Les lignes viennent du catalogue Axonaut. Une prestation hors catalogue n’est jamais bloquée : soit le
      produit est créé dans Axonaut à la validation, soit tu prends le produit le plus proche et tu changes le
      titre, la description et le prix de la ligne.
    </p>
  </div>
</template>
